import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronDown, ChevronUp, FileWarning } from "lucide-react";

/* ========== DEFAULT CONFIGS (SANITARIO) ========== */

const DEFAULT_PROFESSIONI = [
  "Medico Chirurgo", "Odontoiatra", "Medico di Medicina Generale",
  "Pediatra di Libera Scelta", "Medico Specialista", "Infermiere",
  "Infermiere Pediatrico", "Ostetrica/o", "Fisioterapista",
  "Tecnico di Radiologia", "Tecnico di Laboratorio", "Farmacista",
  "Biologo", "Psicologo", "Veterinario", "Dirigente Medico",
  "Direttore Sanitario", "Altro (specificare)",
];

const DEFAULT_CATEGORIE_DANNO = [
  { id: "errore_diagnostico", label: "Errore diagnostico" },
  { id: "errore_terapeutico", label: "Errore terapeutico" },
  { id: "errore_chirurgico", label: "Errore chirurgico" },
  { id: "ritardo_diagnosi", label: "Ritardo nella diagnosi / trattamento" },
  { id: "reazione_avversa", label: "Reazione avversa a farmaci / trattamenti" },
  { id: "infezione_nosocomiale", label: "Infezione nosocomiale" },
  { id: "consenso_informato", label: "Danni da consenso informato inadeguato" },
  { id: "danni_fisici_paziente", label: "Danni fisici subiti dal paziente" },
  { id: "decesso_paziente", label: "Decesso del paziente" },
  { id: "mancato_rispetto_norme", label: "Mancato rispetto norme e regolamenti" },
  { id: "mancato_rispetto_sicurezza", label: "Mancato rispetto normative sulla sicurezza" },
  { id: "altro", label: "Altro (specificare)" },
];

const DEFAULT_INCARICHI = [
  { id: "medico_curante", label: "Medico curante" },
  { id: "medico_specialista", label: "Medico specialista" },
  { id: "chirurgo", label: "Chirurgo operatore" },
  { id: "anestesista", label: "Anestesista / Rianimatore" },
  { id: "primo_operatore", label: "Primo operatore" },
  { id: "aiuto_chirurgo", label: "Aiuto chirurgo" },
  { id: "consulente", label: "Consulente" },
  { id: "direttore_sanitario", label: "Direttore sanitario" },
  { id: "responsabile_reparto", label: "Responsabile di reparto / U.O." },
  { id: "personale_infermieristico", label: "Personale infermieristico" },
  { id: "altro", label: "Altro (specificare)" },
];

const TIPI_NOTIFICA = [
  { id: "atto_citazione", label: "Atto di citazione" },
  { id: "invito_dedurre", label: "Invito a dedurre" },
  { id: "conclusione_indagini", label: "Conclusione indagini preliminari" },
  { id: "informazione_garanzia", label: "Informazione di garanzia" },
  { id: "opposizione_decreto", label: "Opposizione a decreto ingiuntivo / pagamento della parcella al Proponente" },
  { id: "provvedimento_giudiziario", label: "Provvedimento autorità giudiziaria di sospensione, demolizione, sequestro o procedimento d'urgenza ex Art. 700 c.p.c." },
  { id: "mediazione", label: "Ricevimento di istanza di mediazione / negoziazione assistita" },
  { id: "richiesta_risarcimento", label: "Richiesta di risarcimento danni reclamo" },
  { id: "accertamento_tecnico", label: "Ricorso per accertamento tecnico preventivo" },
  { id: "verbale_identificazione", label: "Verbale di identificazione" },
  { id: "altro", label: "Altro (specificare)" },
];

const PROCEDURE_ADOTTATE = [
  { id: "nessuna", label: "Nessuna" },
  { id: "analisi_reporting", label: "Sviluppo di attività di analisi, registrazione e reporting" },
  { id: "pianificazione", label: "Rafforzamento dei processi di pianificazione strategica" },
  { id: "redistribuzione_ruoli", label: "Redistribuzione dei ruoli e delle responsabilità" },
  { id: "training_personale", label: "Training del personale" },
  { id: "riesame_periodico", label: "Riesame periodico e costante dell'efficacia e dell'efficienza dei metodi di controllo applicati" },
  { id: "procedure_uniformi", label: "Sviluppo di procedure uniformi per lo svolgimento delle prestazioni professionali" },
  { id: "altro", label: "Altro (specificare)" },
];

const GRADI_PROCEDIMENTO = [
  { value: "gdp", label: "G.D.P. (Giudice Di Pace)" },
  { value: "tribunale", label: "Tribunale" },
  { value: "corte_appello", label: "Corte d'Appello" },
  { value: "corte_cassazione", label: "Corte di Cassazione" },
];

const ESITI_PENALE = [
  { value: "condanna", label: "Condanna a" },
  { value: "assoluzione", label: "Assoluzione" },
  { value: "proscioglimento", label: "Proscioglimento" },
  { value: "archiviazione", label: "Non luogo a procedere (Archiviazione)" },
  { value: "prescrizione", label: "Prescrizione" },
  { value: "amnistia", label: "Amnistia e indulto" },
  { value: "decreto_penale", label: "Decreto penale di condanna" },
  { value: "patteggiamento", label: "Patteggiamento" },
];

const ESITI_CIVILE = [
  { value: "accoglimento", label: "Accoglimento (quantificare il danno liquidato)" },
  { value: "rigetto", label: "Rigetto" },
  { value: "sentenza_impugnata", label: "Sentenza impugnata da" },
  { value: "accordo_stragiudiziale", label: "Accordo stragiudiziale; risarcimento" },
  { value: "altro", label: "Altro (specificare)" },
];

/* ========== EMPTY CLAIM ========== */

function emptyClaim() {
  return {
    // Sez 1 - Dati Proponente
    professione: "",
    professione_altro: "",
    nome_proponente: "",
    cognome_proponente: "",
    ragione_sociale: "",
    // Sez 2 - Notifica
    data_notifica: "",
    tipi_notifica: [],
    tipo_notifica_altro: "",
    // Sez 3 - Informazioni Rilevanti
    data_evento: "",
    categorie_danno: [],
    categoria_danno_altro: "",
    incarico_professionale: [],
    incarico_altro: "",
    descrizione_evento: "",
    danni_lamentati: "",
    fattori_limitanti: "",
    altre_persone: "",
    ammontare_pretesa: "",
    procedure_adottate: [],
    procedura_altro: "",
    // Sez 4 - Stato del Sinistro
    grado_procedimento: "",
    stato_penale: "",
    imputazione: "",
    esito_penale: "",
    esito_penale_dettagli: "",
    stato_civile: "",
    riserva_assicuratore: "",
    percentuale_responsabilita: "",
    esito_civile: "",
    esito_civile_importo: "",
    sentenza_impugnata_da: "",
    sentenza_nel_grado_di: "",
    esito_civile_dettagli: "",
    // Dichiarazioni
    dichiarazioni_accettate: false,
    dichiarazioni_nome_cognome: "",
    dichiarazioni_data: "",
  };
}

/* ========== MAIN COMPONENT ========== */

export default function ClaimsInfoSheet({ claims, onClaimsChange, config }) {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const professioni = config?.professioni || DEFAULT_PROFESSIONI;
  const categorieDanno = config?.categorieDanno || DEFAULT_CATEGORIE_DANNO;
  const incarichi = config?.incarichi || DEFAULT_INCARICHI;

  const updateClaim = (index, field, value) => {
    const updated = claims.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    onClaimsChange(updated);
  };

  const toggleArrayField = (index, field, itemId) => {
    const claim = claims[index];
    const arr = claim[field] || [];
    const next = arr.includes(itemId) ? arr.filter((x) => x !== itemId) : [...arr, itemId];
    updateClaim(index, field, next);
  };

  const addClaim = () => {
    onClaimsChange([...claims, emptyClaim()]);
    setExpandedIndex(claims.length);
  };

  const removeClaim = (index) => {
    const updated = claims.filter((_, i) => i !== index);
    onClaimsChange(updated);
    if (expandedIndex >= updated.length) setExpandedIndex(Math.max(0, updated.length - 1));
  };

  return (
    <div className="space-y-4" data-testid="claims-info-sheet">
      <div className="flex items-center gap-2 mb-2">
        <FileWarning className="w-5 h-5 text-amber-600" />
        <h3 className="font-heading text-base font-medium text-[#0F1F14]">
          Modulo Raccolta Informazioni Sinistro (CIS)
        </h3>
      </div>
      <p className="text-xs text-[#4A5E51]">
        Compilare un modulo CIS per ciascun sinistro o circostanza da notificare.
      </p>

      {claims.map((claim, idx) => (
        <SingleClaimForm
          key={idx}
          claim={claim}
          index={idx}
          total={claims.length}
          expanded={expandedIndex === idx}
          onToggle={() => setExpandedIndex(expandedIndex === idx ? -1 : idx)}
          onUpdate={(field, value) => updateClaim(idx, field, value)}
          onToggleArray={(field, itemId) => toggleArrayField(idx, field, itemId)}
          onRemove={() => removeClaim(idx)}
          professioni={professioni}
          categorieDanno={categorieDanno}
          incarichi={incarichi}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addClaim}
        className="w-full rounded-xl border-dashed border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5 h-10 text-sm"
        data-testid="add-claim-btn"
      >
        <Plus className="w-4 h-4 mr-2" /> Aggiungi sinistro / circostanza
      </Button>
    </div>
  );
}

export { emptyClaim };

/* ========== SINGLE CLAIM FORM ========== */

function SingleClaimForm({ claim, index, total, expanded, onToggle, onUpdate, onToggleArray, onRemove, professioni, categorieDanno, incarichi }) {
  return (
    <div
      className="border border-[#E5E2DB] rounded-xl overflow-hidden bg-white"
      data-testid={`claim-form-${index}`}
    >
      {/* Header */}
      <div
        className="w-full flex items-center justify-between p-4 bg-[#F5F4F0] hover:bg-[#EDECE8] transition-colors text-left cursor-pointer"
        data-testid={`claim-toggle-${index}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onToggle(); }}
      >
        <span className="font-heading text-sm font-medium text-[#0F1F14]">
          Sinistro / Circostanza n. {index + 1}
        </span>
        <div className="flex items-center gap-2">
          {total > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="p-1 text-red-400 hover:text-red-600 transition-colors"
              data-testid={`claim-remove-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[#4A5E51]" /> : <ChevronDown className="w-4 h-4 text-[#4A5E51]" />}
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className="p-4 sm:p-6 space-y-6">

          {/* ======= SEZ 1: DATI ANAGRAFICI DEL PROPONENTE ======= */}
          <SectionBlock title="Dati Anagrafici del Proponente / Contraente" num="1">
            <CISField label="Professione" required>
              <Select value={claim.professione || ""} onValueChange={(v) => onUpdate("professione", v)}>
                <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid={`claim-${index}-professione`}>
                  <SelectValue placeholder="Seleziona professione" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {professioni.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {claim.professione === "Altro (specificare)" && (
                <Input
                  placeholder="Specificare la professione..."
                  value={claim.professione_altro || ""}
                  onChange={(e) => onUpdate("professione_altro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 mt-2"
                  data-testid={`claim-${index}-professione-altro`}
                />
              )}
            </CISField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CISField label="Nome" required>
                <Input
                  value={claim.nome_proponente || ""}
                  onChange={(e) => onUpdate("nome_proponente", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid={`claim-${index}-nome-prop`}
                />
              </CISField>
              <CISField label="Cognome" required>
                <Input
                  value={claim.cognome_proponente || ""}
                  onChange={(e) => onUpdate("cognome_proponente", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid={`claim-${index}-cognome-prop`}
                />
              </CISField>
            </div>
            <CISField label="Ragione / Denominazione Sociale (in caso di studio)">
              <Input
                value={claim.ragione_sociale || ""}
                onChange={(e) => onUpdate("ragione_sociale", e.target.value)}
                className="rounded-xl border-[#E5E2DB] h-11"
                data-testid={`claim-${index}-ragione-sociale`}
              />
            </CISField>
          </SectionBlock>

          {/* ======= SEZ 2: NOTIFICA DEL SINISTRO / CIRCOSTANZA ======= */}
          <SectionBlock title="Notifica del Sinistro / Circostanza" num="2">
            <CISField label={"Il Sottoscritto dichiara di aver ricevuto / essere venuto a conoscenza di quanto segue in data"} required>
              <Input
                type="date"
                value={claim.data_notifica || ""}
                onChange={(e) => onUpdate("data_notifica", e.target.value)}
                className="rounded-xl border-[#E5E2DB] h-11 max-w-xs"
                data-testid={`claim-${index}-data-notifica`}
              />
            </CISField>

            <CISField label="Tipo di notifica ricevuta" required>
              <div className="grid grid-cols-1 gap-2.5">
                {TIPI_NOTIFICA.map((tipo) => (
                  <div key={tipo.id} className="flex items-start gap-2.5">
                    <Checkbox
                      id={`notifica-${index}-${tipo.id}`}
                      checked={(claim.tipi_notifica || []).includes(tipo.id)}
                      onCheckedChange={() => onToggleArray("tipi_notifica", tipo.id)}
                      className="mt-0.5 data-[state=checked]:bg-[#163321]"
                      data-testid={`claim-${index}-notifica-${tipo.id}`}
                    />
                    <label htmlFor={`notifica-${index}-${tipo.id}`} className="text-sm text-[#0F1F14] cursor-pointer leading-snug">
                      {tipo.label}
                    </label>
                  </div>
                ))}
              </div>
              {(claim.tipi_notifica || []).includes("altro") && (
                <Input
                  placeholder="Specificare..."
                  value={claim.tipo_notifica_altro || ""}
                  onChange={(e) => onUpdate("tipo_notifica_altro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 mt-2"
                  data-testid={`claim-${index}-notifica-altro-text`}
                />
              )}
            </CISField>
          </SectionBlock>

          {/* ======= SEZ 3: INFORMAZIONI RILEVANTI ======= */}
          <SectionBlock title="Informazioni Rilevanti" num="3">
            <CISField label={"Data dell'evento lesivo origine del sinistro / circostanza"} required>
              <Input
                type="date"
                value={claim.data_evento || ""}
                onChange={(e) => onUpdate("data_evento", e.target.value)}
                className="rounded-xl border-[#E5E2DB] h-11 max-w-xs"
                data-testid={`claim-${index}-data-evento`}
              />
            </CISField>

            <CISField label="Indicare in quale categoria ricade il danno lamentato dal terzo" required>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {categorieDanno.map((cat) => (
                  <div key={cat.id} className="flex items-start gap-2.5">
                    <Checkbox
                      id={`danno-${index}-${cat.id}`}
                      checked={(claim.categorie_danno || []).includes(cat.id)}
                      onCheckedChange={() => onToggleArray("categorie_danno", cat.id)}
                      className="mt-0.5 data-[state=checked]:bg-[#163321]"
                      data-testid={`claim-${index}-danno-${cat.id}`}
                    />
                    <label htmlFor={`danno-${index}-${cat.id}`} className="text-sm text-[#0F1F14] cursor-pointer leading-snug">
                      {cat.label}
                    </label>
                  </div>
                ))}
              </div>
              {(claim.categorie_danno || []).includes("altro") && (
                <Input
                  placeholder="Specificare la categoria del danno..."
                  value={claim.categoria_danno_altro || ""}
                  onChange={(e) => onUpdate("categoria_danno_altro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 mt-2"
                  data-testid={`claim-${index}-danno-altro-text`}
                />
              )}
            </CISField>

            {incarichi.length > 0 && (
            <CISField label={"Indicare l'incarico professionale svolto dal Proponente"} required>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {incarichi.map((inc) => (
                  <div key={inc.id} className="flex items-start gap-2.5">
                    <Checkbox
                      id={`incarico-${index}-${inc.id}`}
                      checked={(claim.incarico_professionale || []).includes(inc.id)}
                      onCheckedChange={() => onToggleArray("incarico_professionale", inc.id)}
                      className="mt-0.5 data-[state=checked]:bg-[#163321]"
                      data-testid={`claim-${index}-incarico-${inc.id}`}
                    />
                    <label htmlFor={`incarico-${index}-${inc.id}`} className="text-sm text-[#0F1F14] cursor-pointer leading-snug">
                      {inc.label}
                    </label>
                  </div>
                ))}
              </div>
              {(claim.incarico_professionale || []).includes("altro") && (
                <Input
                  placeholder="Specificare l'incarico..."
                  value={claim.incarico_altro || ""}
                  onChange={(e) => onUpdate("incarico_altro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 mt-2"
                  data-testid={`claim-${index}-incarico-altro-text`}
                />
              )}
            </CISField>
            )}

            <CISField label={"Descrizione dell'evento lesivo origine del sinistro / circostanza"} required>
              <Textarea
                value={claim.descrizione_evento || ""}
                onChange={(e) => onUpdate("descrizione_evento", e.target.value)}
                className="rounded-xl border-[#E5E2DB] min-h-[100px]"
                placeholder="Descrizione dettagliata dell'errore o dell'omissione..."
                data-testid={`claim-${index}-descrizione`}
              />
            </CISField>

            <CISField label="Danno/i lamentato/i dal danneggiato / reclamante">
              <Textarea
                value={claim.danni_lamentati || ""}
                onChange={(e) => onUpdate("danni_lamentati", e.target.value)}
                className="rounded-xl border-[#E5E2DB] min-h-[80px]"
                placeholder="Danni dichiarati dal paziente / reclamante..."
                data-testid={`claim-${index}-danni-lamentati`}
              />
            </CISField>

            <CISField label={"Eventuali fattori / dettagli che limitano le responsabilità del Proponente"}>
              <Textarea
                value={claim.fattori_limitanti || ""}
                onChange={(e) => onUpdate("fattori_limitanti", e.target.value)}
                className="rounded-xl border-[#E5E2DB] min-h-[80px]"
                placeholder="Indicare eventuali circostanze attenuanti..."
                data-testid={`claim-${index}-fattori-limitanti`}
              />
            </CISField>

            <CISField label={"Altre persone coinvolte (generalità, ruolo, responsabilità)"}>
              <Textarea
                value={claim.altre_persone || ""}
                onChange={(e) => onUpdate("altre_persone", e.target.value)}
                className="rounded-xl border-[#E5E2DB] min-h-[80px]"
                placeholder="Nome, ruolo e responsabilità di eventuali altri soggetti coinvolti..."
                data-testid={`claim-${index}-altre-persone`}
              />
            </CISField>

            <CISField label="Ammontare pretesa risarcitoria" required>
              <div className="relative max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51]">&euro;</span>
                <Input
                  type="number"
                  value={claim.ammontare_pretesa || ""}
                  onChange={(e) => onUpdate("ammontare_pretesa", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 pl-8"
                  placeholder="0,00"
                  data-testid={`claim-${index}-ammontare`}
                />
              </div>
            </CISField>

            <CISField label="Quali procedure, volte ad evitare il ripetersi dei fatti causa del sinistro in oggetto, sono state introdotte dal Proponente?">
              <div className="grid grid-cols-1 gap-2.5">
                {PROCEDURE_ADOTTATE.map((proc) => (
                  <div key={proc.id} className="flex items-start gap-2.5">
                    <Checkbox
                      id={`proc-${index}-${proc.id}`}
                      checked={(claim.procedure_adottate || []).includes(proc.id)}
                      onCheckedChange={() => onToggleArray("procedure_adottate", proc.id)}
                      className="mt-0.5 data-[state=checked]:bg-[#163321]"
                      data-testid={`claim-${index}-proc-${proc.id}`}
                    />
                    <label htmlFor={`proc-${index}-${proc.id}`} className="text-sm text-[#0F1F14] cursor-pointer leading-snug">
                      {proc.label}
                    </label>
                  </div>
                ))}
              </div>
              {(claim.procedure_adottate || []).includes("altro") && (
                <Input
                  placeholder="Specificare..."
                  value={claim.procedura_altro || ""}
                  onChange={(e) => onUpdate("procedura_altro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11 mt-2"
                  data-testid={`claim-${index}-proc-altro-text`}
                />
              )}
            </CISField>
          </SectionBlock>

          {/* ======= SEZ 4: STATO DEL SINISTRO ======= */}
          <SectionBlock title="Stato del Sinistro" num="4">

            {/* 4.1 Grado del procedimento */}
            <CISField label="4.1 - Grado del procedimento">
              <RadioGroup
                value={claim.grado_procedimento || ""}
                onValueChange={(v) => onUpdate("grado_procedimento", v)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
              >
                {GRADI_PROCEDIMENTO.map((g) => (
                  <div key={g.value} className="flex items-center gap-2">
                    <RadioGroupItem value={g.value} id={`grado-${index}-${g.value}`} data-testid={`claim-${index}-grado-${g.value}`} />
                    <Label htmlFor={`grado-${index}-${g.value}`} className="text-sm cursor-pointer">{g.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </CISField>

            {/* 4.2 Stato del procedimento */}
            <div className="pt-2">
              <Label className="text-sm font-medium text-[#0F1F14]">4.2 - Stato del procedimento</Label>
            </div>

            {/* Procedimento Penale */}
            <CISField label="Procedimento penale">
              <RadioGroup
                value={claim.stato_penale || ""}
                onValueChange={(v) => onUpdate("stato_penale", v)}
                className="space-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="in_corso" id={`penale-${index}-in-corso`} data-testid={`claim-${index}-penale-in-corso`} />
                  <Label htmlFor={`penale-${index}-in-corso`} className="text-sm cursor-pointer">Procedimento penale in corso</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="concluso" id={`penale-${index}-concluso`} data-testid={`claim-${index}-penale-concluso`} />
                  <Label htmlFor={`penale-${index}-concluso`} className="text-sm cursor-pointer">Procedimento penale concluso con</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="non_applicabile" id={`penale-${index}-na`} data-testid={`claim-${index}-penale-na`} />
                  <Label htmlFor={`penale-${index}-na`} className="text-sm cursor-pointer">Non applicabile</Label>
                </div>
              </RadioGroup>
            </CISField>

            {claim.stato_penale === "in_corso" && (
              <div className="pl-4 border-l-2 border-[#163321]/10">
                <CISField label="Imputazione">
                  <Input
                    value={claim.imputazione || ""}
                    onChange={(e) => onUpdate("imputazione", e.target.value)}
                    className="rounded-xl border-[#E5E2DB] h-11"
                    placeholder="Descrizione dell'imputazione..."
                    data-testid={`claim-${index}-imputazione`}
                  />
                </CISField>
              </div>
            )}

            {claim.stato_penale === "concluso" && (
              <div className="pl-4 border-l-2 border-[#163321]/10 space-y-3">
                <CISField label="Procedimento penale concluso con">
                  <RadioGroup
                    value={claim.esito_penale || ""}
                    onValueChange={(v) => onUpdate("esito_penale", v)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
                  >
                    {ESITI_PENALE.map((e) => (
                      <div key={e.value} className="flex items-center gap-2">
                        <RadioGroupItem value={e.value} id={`esito-pen-${index}-${e.value}`} data-testid={`claim-${index}-esito-pen-${e.value}`} />
                        <Label htmlFor={`esito-pen-${index}-${e.value}`} className="text-sm cursor-pointer">{e.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CISField>
                {(claim.esito_penale === "condanna" || claim.esito_penale === "patteggiamento" || claim.esito_penale === "decreto_penale") && (
                  <CISField label="Dettagli della conclusione">
                    <Input
                      value={claim.esito_penale_dettagli || ""}
                      onChange={(e) => onUpdate("esito_penale_dettagli", e.target.value)}
                      className="rounded-xl border-[#E5E2DB] h-11"
                      placeholder="Dettagli della condanna / patteggiamento..."
                      data-testid={`claim-${index}-esito-pen-dettagli`}
                    />
                  </CISField>
                )}
              </div>
            )}

            {/* Procedimento Civile */}
            <CISField label="Procedimento civile">
              <RadioGroup
                value={claim.stato_civile || ""}
                onValueChange={(v) => onUpdate("stato_civile", v)}
                className="space-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="in_corso" id={`civile-${index}-in-corso`} data-testid={`claim-${index}-civile-in-corso`} />
                  <Label htmlFor={`civile-${index}-in-corso`} className="text-sm cursor-pointer">Procedimento civile in corso</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="concluso" id={`civile-${index}-concluso`} data-testid={`claim-${index}-civile-concluso`} />
                  <Label htmlFor={`civile-${index}-concluso`} className="text-sm cursor-pointer">Procedimento civile concluso con</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="non_applicabile" id={`civile-${index}-na`} data-testid={`claim-${index}-civile-na`} />
                  <Label htmlFor={`civile-${index}-na`} className="text-sm cursor-pointer">Non applicabile</Label>
                </div>
              </RadioGroup>
            </CISField>

            {claim.stato_civile === "in_corso" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-4 border-l-2 border-[#163321]/10">
                <CISField label={"Riserva predisposta dall'assicuratore"}>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51]">&euro;</span>
                    <Input
                      type="number"
                      value={claim.riserva_assicuratore || ""}
                      onChange={(e) => onUpdate("riserva_assicuratore", e.target.value)}
                      className="rounded-xl border-[#E5E2DB] h-11 pl-8"
                      placeholder="0,00"
                      data-testid={`claim-${index}-riserva`}
                    />
                  </div>
                </CISField>
                <CISField label={"Percentuale di responsabilità del Proponente quantificata dalla CTU"}>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={claim.percentuale_responsabilita || ""}
                      onChange={(e) => onUpdate("percentuale_responsabilita", e.target.value)}
                      className="rounded-xl border-[#E5E2DB] h-11 pr-8"
                      placeholder="0"
                      data-testid={`claim-${index}-percentuale`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51]">%</span>
                  </div>
                </CISField>
              </div>
            )}

            {claim.stato_civile === "concluso" && (
              <div className="pl-4 border-l-2 border-[#163321]/10 space-y-3">
                <CISField label="Procedimento civile concluso con">
                  <RadioGroup
                    value={claim.esito_civile || ""}
                    onValueChange={(v) => onUpdate("esito_civile", v)}
                    className="space-y-2.5"
                  >
                    {ESITI_CIVILE.map((e) => (
                      <div key={e.value} className="flex items-center gap-2">
                        <RadioGroupItem value={e.value} id={`esito-civ-${index}-${e.value}`} data-testid={`claim-${index}-esito-civ-${e.value}`} />
                        <Label htmlFor={`esito-civ-${index}-${e.value}`} className="text-sm cursor-pointer">{e.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CISField>

                {/* Accoglimento - importo */}
                {claim.esito_civile === "accoglimento" && (
                  <CISField label="Danno liquidato">
                    <div className="relative max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51]">&euro;</span>
                      <Input
                        type="number"
                        value={claim.esito_civile_importo || ""}
                        onChange={(e) => onUpdate("esito_civile_importo", e.target.value)}
                        className="rounded-xl border-[#E5E2DB] h-11 pl-8"
                        placeholder="0,00"
                        data-testid={`claim-${index}-civ-importo`}
                      />
                    </div>
                  </CISField>
                )}

                {/* Sentenza impugnata - da chi + nel grado di */}
                {claim.esito_civile === "sentenza_impugnata" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <CISField label="Sentenza impugnata da">
                      <Input
                        value={claim.sentenza_impugnata_da || ""}
                        onChange={(e) => onUpdate("sentenza_impugnata_da", e.target.value)}
                        className="rounded-xl border-[#E5E2DB] h-11"
                        placeholder="Indicare da chi..."
                        data-testid={`claim-${index}-sentenza-da`}
                      />
                    </CISField>
                    <CISField label="Nel grado di">
                      <Input
                        value={claim.sentenza_nel_grado_di || ""}
                        onChange={(e) => onUpdate("sentenza_nel_grado_di", e.target.value)}
                        className="rounded-xl border-[#E5E2DB] h-11"
                        placeholder="Grado di appello..."
                        data-testid={`claim-${index}-sentenza-grado`}
                      />
                    </CISField>
                  </div>
                )}

                {/* Accordo stragiudiziale - importo */}
                {claim.esito_civile === "accordo_stragiudiziale" && (
                  <CISField label="Risarcimento">
                    <div className="relative max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51]">&euro;</span>
                      <Input
                        type="number"
                        value={claim.esito_civile_importo || ""}
                        onChange={(e) => onUpdate("esito_civile_importo", e.target.value)}
                        className="rounded-xl border-[#E5E2DB] h-11 pl-8"
                        placeholder="0,00"
                        data-testid={`claim-${index}-civ-risarcimento`}
                      />
                    </div>
                  </CISField>
                )}

                {/* Altro - specificare */}
                {claim.esito_civile === "altro" && (
                  <CISField label="Specificare">
                    <Input
                      value={claim.esito_civile_dettagli || ""}
                      onChange={(e) => onUpdate("esito_civile_dettagli", e.target.value)}
                      className="rounded-xl border-[#E5E2DB] h-11"
                      placeholder="Specificare..."
                      data-testid={`claim-${index}-civ-dettagli`}
                    />
                  </CISField>
                )}
              </div>
            )}
          </SectionBlock>

          {/* ======= SEZ 5: DICHIARAZIONI ======= */}
          <SectionBlock title="Dichiarazioni" num="5">
            <div className="p-4 bg-[#F5F4F0] rounded-xl text-xs text-[#4A5E51] leading-relaxed space-y-2">
              <p>
                Ai sensi di legge, il Dichiarante prende atto che tutti i dati riportati nel presente documento saranno acquisiti esclusivamente per le finalit&agrave; di cui al contratto di copertura assicurativa stipulato con Lloyd&rsquo;s.
              </p>
              <p>
                Il Dichiarante, consapevole che eventuali omissioni, false attestazioni e/o reticenze possono comportare l&rsquo;inoperativit&agrave; della garanzia assicurativa, dichiara che le informazioni fornite sono complete e veritiere.
              </p>
            </div>

            <div className="flex items-start gap-3 mt-3">
              <Checkbox
                id={`dichiarazioni-${index}`}
                checked={claim.dichiarazioni_accettate || false}
                onCheckedChange={(v) => onUpdate("dichiarazioni_accettate", v)}
                className="mt-0.5 data-[state=checked]:bg-[#163321]"
                data-testid={`claim-${index}-dichiarazioni-check`}
              />
              <label htmlFor={`dichiarazioni-${index}`} className="text-sm text-[#0F1F14] cursor-pointer leading-snug">
                Dichiaro che le informazioni fornite sono complete e veritiere <span className="text-[#D97B58]">*</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <CISField label="Nome e Cognome" required>
                <Input
                  value={claim.dichiarazioni_nome_cognome || ""}
                  onChange={(e) => onUpdate("dichiarazioni_nome_cognome", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid={`claim-${index}-dich-nome`}
                />
              </CISField>
              <CISField label="Data" required>
                <Input
                  type="date"
                  value={claim.dichiarazioni_data || ""}
                  onChange={(e) => onUpdate("dichiarazioni_data", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid={`claim-${index}-dich-data`}
                />
              </CISField>
            </div>
          </SectionBlock>

        </div>
      )}
    </div>
  );
}

/* ========== HELPER SUB-COMPONENTS ========== */

function SectionBlock({ title, num, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-[#E5E2DB]">
        <span className="w-6 h-6 rounded-full bg-[#163321] text-white text-xs flex items-center justify-center font-medium shrink-0">{num}</span>
        <h4 className="font-heading text-sm font-medium text-[#0F1F14]">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function CISField({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-[#0F1F14]">
        {label} {required && <span className="text-[#D97B58]">*</span>}
      </Label>
      {children}
    </div>
  );
}
